import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import useExpenseLookups from '../../components/expense/useExpenseLookups';
import { formatDate } from '../../services/expenseVouchersApi';
import { formatMoney, PAYMENT_ROW_STATUS_META, reportsApi } from '../../services/expenseAccountsApi';
import { useAccountsBase } from '../../components/expense/voucherShared';
import { DateRangeFilter, EmployeeFilter } from '../../components/expense/AccountsFilters';
import {
  CurrencyTotals, ExportButton, FilterBar, ReportsHeader, ReportTable, useReport,
} from '../../components/expense/ReportShared';

// Successful payments by default; pending and voided rows can be shown for audit
const EMPTY_FILTERS = { vendor: '', employeeId: '', currency: '', rowStatus: 'PAID', from: '', to: '' };

const ROW_STATUS_OPTIONS = [
  { value: 'PAID', label: 'Paid (successful)' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'VOIDED', label: 'Voided' },
  { value: '', label: 'All' },
];

// Accounts Payment Register: one row per payment / instalment. A voided payment is never
// counted as paid; totals are per currency.
const AccountsPaymentRegister = () => {
  const navigate = useNavigate();
  const accountsBase = useAccountsBase();
  const { currencies, users } = useExpenseLookups(['currencies', 'users']);
  const report = useReport(reportsApi.payments, { emptyFilters: EMPTY_FILTERS, defaultSort: { sort: 'date', order: 'desc' } });
  const { filters, setFilter, setFilterValue } = report;

  const columns = [
    { label: 'Payment Date', sortKey: 'date', nowrap: true, render: (r) => formatDate(r.date) },
    { label: 'Voucher No.', sortKey: 'voucherNumber', nowrap: true, render: (r) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.voucherNumber}</Typography> },
    { label: 'Employee', sortKey: 'employee', render: (r) => r.employeeName },
    { label: 'Vendor', render: (r) => r.vendors.join(', ') || '-' },
    { label: 'Currency', sortKey: 'currency', nowrap: true, render: (r) => r.currency },
    {
      label: 'Amount', sortKey: 'amount', nowrap: true, align: 'right',
      render: (r) => (
        <Typography variant="body2" sx={r.status === 'VOIDED' ? { textDecoration: 'line-through', color: 'text.secondary' } : { fontWeight: 600 }}>
          {formatMoney(r.amount, r.currency)}
        </Typography>
      ),
    },
    {
      label: 'Status', sortKey: 'status',
      render: (r) => {
        const meta = PAYMENT_ROW_STATUS_META[r.status] || { label: r.status, color: 'default' };
        const chip = <Chip size="small" label={meta.label} color={meta.color} variant={r.status === 'PAID' ? 'filled' : 'outlined'} />;
        return r.status === 'VOIDED' ? <Tooltip title={`Voided ${formatDate(r.voidedAt)}${r.voidedByName ? ` by ${r.voidedByName}` : ''}: ${r.voidReason || ''}`}>{chip}</Tooltip> : chip;
      },
    },
    { label: 'Paid Via', render: (r) => r.paidVia.join(', ') || '-' },
    { label: 'Narration', render: (r) => r.narration || r.voucherNarration || '-' },
    { label: 'Accounts User', render: (r) => r.paidByName || r.createdByName || '-' },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <ReportsHeader
        title="Payment Register"
        subtitle="One row per payment / instalment. Voided payments are never counted as paid."
        actions={<ExportButton onExport={reportsApi.exportPayments} params={report.params} />}
      />

      <FilterBar onClear={report.clear}>
        <TextField size="small" placeholder="Search voucher, employee, vendor, project..." value={report.search} onChange={(e) => report.setSearch(e.target.value)} sx={{ minWidth: 280 }} />
        <EmployeeFilter users={users} value={filters.employeeId} onChange={setFilterValue('employeeId')} />
        <TextField size="small" label="Vendor" placeholder="Vendor name" value={filters.vendor} onChange={setFilter('vendor')} sx={{ minWidth: 170 }} />
        <TextField select size="small" label="Currency" value={filters.currency} onChange={setFilter('currency')} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>
          {currencies.map((c) => <MenuItem key={c.value} value={c.value}>{c.value}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Payment status" value={filters.rowStatus} onChange={setFilter('rowStatus')} sx={{ minWidth: 170 }}>
          {ROW_STATUS_OPTIONS.map((s) => <MenuItem key={s.value || 'all'} value={s.value}>{s.label}</MenuItem>)}
        </TextField>
        <DateRangeFilter label="Payment date" from={filters.from} to={filters.to} onFromChange={setFilterValue('from')} onToChange={setFilterValue('to')} />
      </FilterBar>

      <CurrencyTotals
        testId="payments-totals"
        summary={report.summary}
        title={(s) => `${s.currency} - Paid ${formatMoney(s.paid, s.currency)}`}
        lines={(s) => [
          `${s.paidCount} successful payment${s.paidCount === 1 ? '' : 's'}`,
          s.pendingCount ? `Pending ${formatMoney(s.pending, s.currency)} (${s.pendingCount})` : null,
          s.voidedCount ? `Voided ${formatMoney(s.voided, s.currency)} (${s.voidedCount}) - not counted` : null,
        ]}
      />

      <ReportTable report={report} columns={columns} emptyText="No payments match." onRowClick={(r) => navigate(`${accountsBase}/${r.voucherId}`)} />
    </Box>
  );
};

export default AccountsPaymentRegister;
